import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { BalanceProvider } from "@/lib/balance-context";
import Navbar from "@/components/Navbar";
import DemoBanner from "@/components/DemoBanner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CreateCapsule from "@/pages/CreateCapsule";
import CapsuleDetail from "@/pages/CapsuleDetail";
import VerifyRecipient from "@/pages/VerifyRecipient";
import WatchCapsule from "@/pages/WatchCapsule";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AdminPage, { AdminProtected } from "@/pages/Admin";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-white/40">Loading vault...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-white/40">Loading...</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppShell() {
  return (
    <>
      <Navbar />
      <DemoBanner />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/capsules/new" element={<Protected><CreateCapsule /></Protected>} />
          <Route path="/capsules/:id" element={<Protected><CapsuleDetail /></Protected>} />
          <Route path="/verify/:token" element={<VerifyRecipient />} />
          <Route path="/watch/:token" element={<WatchCapsule />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin/*" element={<AdminProtected><AdminPage /></AdminProtected>} />
          <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
          <Route path="/billing/cancel" element={<Protected><BillingCancel /></Protected>} />
        </Routes>
      </main>
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#121212", color: "#F4F1DE", border: "1px solid rgba(255,255,255,0.1)" } }} />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <BalanceProvider>
            <AppShell />
          </BalanceProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
